import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import ProgressSteps from "../../../components/ProgressSteps";
import { getEtablissementDirectory, type PublicEtablissementSummary } from "../../../api/authEtablissement";

export default function ProSignupEtablissements() {
  const navigate = useNavigate();
  const [directory, setDirectory] = useState<PublicEtablissementSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<string[]>(() => {
    try {
      const raw = localStorage.getItem("pro.signup");
      if (!raw) return [];
      const draft = JSON.parse(raw) as Record<string, unknown>;
      return Array.isArray(draft.etablissement_ids) ? (draft.etablissement_ids as string[]) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    let cancelled = false;
    void getEtablissementDirectory({ estRevendique: true, limit: 50 }).then((res) => {
      if (cancelled) return;
      setLoading(false);
      if (res.ok) setDirectory(res.data.data);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const results = useMemo(() => {
    const trimmed = query.trim().toLowerCase();
    return trimmed === "" ? directory : directory.filter((e) => e.nom.toLowerCase().includes(trimmed));
  }, [directory, query]);

  const toggle = (id: string) => {
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const proceed = () => {
    try {
      const raw = localStorage.getItem("pro.signup");
      const base = raw ? JSON.parse(raw) : {};
      localStorage.setItem("pro.signup", JSON.stringify({ ...base, etablissement_ids: selected }));
    } catch {
      /* ignore */
    }
    navigate("/professionnel/inscription/document");
  };

  return (
    <div className="relative h-screen overflow-hidden">
      <img src={import.meta.env.BASE_URL + 'maps-reset-side.jpg'} alt="pro-signup-etablissements" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-black/30" />

      <div className="absolute inset-0 z-10 px-4 md:px-8 py-6 md:py-8 flex">
        <div className="max-w-6xl mx-auto flex flex-col w-full">
          <div className="pl-2 md:pl-6 pt-2 md:pt-4">
            <Link to="/professionnel/inscription/identifiants" className="inline-flex items-center gap-2 text-white/90">
              <ArrowLeft size={18} /> Retour
            </Link>
          </div>

          <div className="mt-auto flex items-end justify-between gap-6 pl-2 md:pl-6 pr-4 md:pr-8 pb-4 md:pb-8">
            <div className="w-full max-w-2xl min-h-[520px] bg-white/95 backdrop-blur rounded-[12px] shadow p-8 md:p-10">
              <h1 className="text-2xl font-semibold" style={{ color: '#0B1F16' }}>Vos établissements</h1>
              <p className="mt-2 text-sm text-gray-600">
                Sélectionnez le ou les établissements où vous exercez (facultatif). Chaque demande devra être validée par
                l'établissement concerné avant de devenir active.
              </p>

              <div className="mt-6 space-y-3">
                <input
                  placeholder="Rechercher un établissement…"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  className="w-full h-12 rounded-[10px] border-2 px-3 text-base"
                  style={{ borderColor: '#D1E2DD' }}
                />

                <div className="max-h-64 overflow-y-auto space-y-2 thin-scrollbar">
                  {loading && <div className="text-sm text-gray-500 py-2">Chargement…</div>}
                  {!loading && results.length === 0 && (
                    <div className="text-sm text-gray-500 py-2">Aucun établissement trouvé.</div>
                  )}
                  {!loading &&
                    results.map((item) => (
                      <label
                        key={item.id}
                        className="flex items-center gap-3 rounded-[10px] border px-3 py-2 cursor-pointer"
                        style={{ borderColor: '#D1E2DD' }}
                      >
                        <input
                          type="checkbox"
                          checked={selected.includes(item.id)}
                          onChange={() => toggle(item.id)}
                        />
                        <div>
                          <div className="text-sm font-medium" style={{ color: '#0B1F16' }}>
                            {item.nom}
                            {item.sigle ? ` (${item.sigle})` : ""}
                          </div>
                          <div className="text-xs text-gray-500">
                            {item.type} — {item.ville ?? item.commune ?? "Localisation inconnue"}
                          </div>
                        </div>
                      </label>
                    ))}
                </div>
              </div>

              <div className="mt-6 space-y-3">
                <button
                  type="button"
                  onClick={proceed}
                  className="w-full h-14 rounded-[10px] font-medium cursor-pointer"
                  style={{ backgroundColor: '#00553E', color: '#FFFFFF' }}
                >
                  Suivant{selected.length > 0 ? ` (${selected.length} sélectionné${selected.length > 1 ? "s" : ""})` : ""}
                </button>
                <button
                  type="button"
                  onClick={proceed}
                  className="w-full h-10 rounded-[10px] font-medium cursor-pointer text-sm"
                  style={{ color: '#0B1F16' }}
                >
                  Passer cette étape
                </button>
              </div>
            </div>

            <div className="hidden md:block shrink-0">
              <img src={import.meta.env.BASE_URL + 'Group 5.png'} alt="agabaza-logo" className="w-auto" style={{ height: 144 }} />
            </div>
          </div>

          <div className="pl-2 md:pl-6 pt-2 md:pt-4">
            <ProgressSteps step={3} total={4} />
          </div>
        </div>
      </div>
    </div>
  );
}
